import { ActionTree } from "vuex";
import { TeamsState, TeamState, TeamStudentState } from "./state";
import { RemoteTeachingService, StudentService, TeacherService, TeamDto } from "@/services";
import { Logger } from "@/utils/logger";
import { createGuid } from "@/utils/utils";
import { defineVuexName, MODULES, vuexName } from "../utils";

//* Serialize */
const dtoToState = (teams: TeamDto[], allStudents: TeamStudentState[]): TeamState[] =>
  teams.map(({ id, name, color, studentIds, order }) => {
    const students = allStudents.filter((student) => studentIds.findIndex((id) => id === student.id) > -1);
    return {
      id: id ?? createGuid(),
      name,
      color,
      order,
      students,
      isNew: false,
    };
  });
//* Deserialize */
const stateToDto = (teams: TeamState[]): TeamDto[] =>
  teams.map(({ name, color, isNew, id, students, order }) => ({
    name,
    color,
    order,
    id: isNew ? null : id,
    studentIds: students.map((s) => s.id),
  }));

const actions: ActionTree<TeamsState, any> = {
  //getAllStudents
  async [defineVuexName(MODULES.TEAMS.DISPATCHES.GET_ALL_STUDENTS)]({ commit }, payload: { groupId: string; classId: string }) {
    const { groupId, classId } = payload;
    try {
      const stResponse = await TeacherService.fetchStudentsByGroupId({ groupId, classId });
      if (!stResponse) return;
      commit(vuexName(MODULES.TEACHER.COMMITS.SET_CLASS_SET_UP_STUDENTS), stResponse, { root: true });
      const avResponse = await StudentService.fetchListAvatars(stResponse.map((st) => st.studentId));
      commit(
        vuexName(MODULES.TEAMS.COMMITS.SET_ALL_STUDENTS, false),
        stResponse
          .filter(({ isActivated }) => isActivated)
          .map(({ studentId, studentName }) => ({
            name: studentName,
            id: studentId,
            avatarUrl: avResponse.find((av) => av.id === studentId)?.avatar ?? "",
          })),
      );
    } catch (err) {
      Logger.error(err);
    }
  },
  //getTeams
  async [defineVuexName(MODULES.TEAMS.DISPATCHES.GET_TEAMS)]({ rootState, commit, state, dispatch }) {
    const { sessionId, classId, groupId } = rootState.classTeaching;
    commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_LOADING, false), true);
    try {
      if (!state.allStudentsFetched) await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.GET_ALL_STUDENTS, false), { groupId, classId });
      const response = await RemoteTeachingService.fetchTeams({ sessionId });
      commit(vuexName(MODULES.TEAMS.COMMITS.SET_TEAMS, false), dtoToState(response.data.teams, state.allStudents));
      commit(vuexName(MODULES.TEAMS.COMMITS.SET_TEAMS_REVISION, false), response.data.revision);
    } catch (err) {
      Logger.error(err);
    } finally {
      commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_LOADING, false), false);
    }
  },
  //updateTeamsAsync
  async [defineVuexName(MODULES.TEAMS.DISPATCHES.UPDATE_TEAMS_ASYNC)]({ rootState, commit }, teams: TeamState[]) {
    try {
      const sessionId = rootState.classTeaching.sessionId;
      if (!sessionId) return commit(vuexName(MODULES.TEAMS.COMMITS.SET_TEAMS, false), teams);
      await RemoteTeachingService.putTeams({ sessionId, teams: stateToDto(teams), revision: rootState.teams.revision });
      commit(vuexName(MODULES.TEAMS.COMMITS.SET_TEAMS, false), teams);
    } catch (err) {
      throw new Error("Update team failed!");
    }
  },
  //toggleTeamModeAsync
  async [defineVuexName(MODULES.TEAMS.DISPATCHES.TOGGLE_TEAM_MODE_ASYNC)]({ rootState, commit }, isOpen: boolean) {
    try {
      const sessionId = rootState.classTeaching.sessionId;
      if (!sessionId) return;
      await RemoteTeachingService.putToggleTeamMode({ sessionId, isOpen });
      commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_TEAM_MODE, false), isOpen);
    } catch (err) {
      throw new Error("Toggle team failed!");
    }
  },
  //onSettingModalSave
  async [defineVuexName(MODULES.TEAMS.DISPATCHES.ON_SETTING_MODAL_SAVE)](
    { commit, dispatch, state },
    payload: { teams: TeamState[]; hasChanges: boolean },
  ) {
    try {
      if (payload.hasChanges) {
        await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.UPDATE_TEAMS_ASYNC, false), payload.teams);
      }
      state.enableTeamModeOnSave && (await dispatch(vuexName(MODULES.TEAMS.DISPATCHES.TOGGLE_TEAM_MODE_ASYNC, false), true));
      commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_SETTING_MODAL_OPEN, false), false);
      commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_ENABLE_TEAM_MODE_ON_SAVE, false), false);
    } catch (err) {
      Logger.error(err);
    }
  },
  //onSettingModalCancel
  async [defineVuexName(MODULES.TEAMS.DISPATCHES.ON_SETTING_MODAL_CANCEL)]({ commit }) {
    commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_SETTING_MODAL_OPEN, false), false);
    commit(vuexName(MODULES.TEAMS.COMMITS.SET_IS_ENABLE_TEAM_MODE_ON_SAVE, false), false);
  },
};

export default actions;
